import type {
  DataObjectType,
  DataObjectRaw,
  DataObjectFieldsSchema,
  DataObjectFieldsSchemaRaw,
  DataObjectUISchema,
  DataObjectUISchemaRaw,
  DataObjectUISchemaFieldRaw,
  DataObjectFilterConfigRaw,
  DataObjectFieldRaw,
  DataObjectField,
  DataObjectUISchemaField,
  DataObjectChatConfigRangeConfig,
  DataObjectChatConfigRangeConfigRaw,
  DataObjectFilterConfig,
  DataObjectUISchemaSection,
  DataObjectUISchemaSectionRaw,
  DataObjectDetailUISchemaTab,
  DataObjectDetailUISchemaTabRaw,
  DataObjectFilterConfigSelectOption,
  DataObjectFilterConfigSelectOptionRaw,
  DataObjectDetailUISchemaRaw,
  DataObjectDetailUISchema,
  DataObjectWorkflowViewConfig,
  DataObjectWorkflowViewConfigRaw
} from "@certa/types/src/dataObjects.types";

/**
 * Reverse parse x-rollup field from snake_case to camelCase with lossless transformation
 */
export const reverseParseXRollup = (
  xRollup: DataObjectFieldRaw["x-rollup"]
): DataObjectField["x-rollup"] => {
  if (!xRollup) return undefined;

  const {
    from_object_type: fromObjectType,
    forward_relation_name: forwardRelationName,
    source_field_path: sourceFieldPath,
    operation_type: operationType,
    ...unknownProps
  } = xRollup;

  const result: DataObjectField["x-rollup"] = {
    fromObjectType,
    forwardRelationName,
    sourceFieldPath,
    operationType,
    ...unknownProps
  };

  return result;
};

/**
 * Reverse parse x-relationship field from snake_case to camelCase with lossless transformation
 */
export const reverseParseXRelationship = (
  xRelationship: DataObjectFieldRaw["x-relationship"]
): DataObjectField["x-relationship"] => {
  if (!xRelationship) return undefined;

  const {
    to_object_type: toObjectType,
    relationship_type: relationshipType,
    reverse_relation_name: reverseRelationName,
    ...unknownProps
  } = xRelationship;

  const result: DataObjectField["x-relationship"] = {
    toObjectType,
    relationshipType,
    reverseRelationName,
    ...unknownProps
  };

  return result;
};

/**
 * Reverse parse a single DataObjectField from snake_case to camelCase with lossless transformation
 */
const reverseParseDataObjectField = (
  field: DataObjectFieldRaw
): DataObjectField => {
  const {
    indexed: isIndexed,
    uniqueItems: hasUniqueItems,
    "x-rollup": xRollup,
    "x-relationship": xRelationship,
    ...restFieldProps
  } = field;

  const result: DataObjectField = {
    ...restFieldProps,
    __isFrozen: false // Default value TODO: Figure out if this is correct.
  };

  if (isIndexed !== undefined) {
    result.isIndexed = isIndexed;
  }

  if (hasUniqueItems !== undefined) {
    result.hasUniqueItems = hasUniqueItems;
  }

  if (xRollup) {
    result["x-rollup"] = reverseParseXRollup(xRollup);
  }

  if (xRelationship) {
    result["x-relationship"] = reverseParseXRelationship(xRelationship);
  }

  return result;
};

/**
 * Reverse parse DataObjectFieldsSchemaRaw from snake_case to camelCase
 */
const reverseParseDataObjectSchema = (
  schema: DataObjectFieldsSchemaRaw
): DataObjectFieldsSchema => {
  const properties: DataObjectFieldsSchema["properties"] = {};

  const { properties: schemaProperties, required, ...unknownProps } = schema;
  for (const [fieldTag, field] of Object.entries(schemaProperties)) {
    properties[fieldTag] = reverseParseDataObjectField(field);
  }
  return {
    properties,
    required,
    ...unknownProps
  };
};

/**
 * Reverse parse range config from snake_case to camelCase with lossless transformation
 */
const reverseParseRangeConfig = (
  rangeConfig: DataObjectChatConfigRangeConfigRaw
): DataObjectChatConfigRangeConfig => {
  const { max_range: maxRange, ranges, ...unknownProps } = rangeConfig;

  const result: DataObjectChatConfigRangeConfig = {
    ...unknownProps
  };

  if (maxRange !== undefined) {
    result.maxRange = maxRange;
  }

  if (ranges !== undefined) {
    result.ranges = ranges;
  }

  return result;
};

/**
 * Reverse parse chart config from snake_case to camelCase with lossless transformation
 */
const reverseParseChartConfig = (
  chartConfig: DataObjectUISchemaFieldRaw["chart_config"]
): DataObjectUISchemaField["chartConfig"] => {
  if (!chartConfig) return undefined;

  const {
    identifier_field: identifierField,
    range_config: rangeConfig,
    data_points: dataPoints,
    x_axis: xAxis,
    y_axis: yAxis,
    ...unknownProps
  } = chartConfig;

  const result: DataObjectUISchemaField["chartConfig"] = {
    ...unknownProps
  };

  if (identifierField !== undefined) {
    result.identifierField = identifierField;
  }

  if (rangeConfig) {
    result.rangeConfig = reverseParseRangeConfig(rangeConfig);
  }

  if (dataPoints !== undefined) {
    result.dataPoints = dataPoints;
  }

  if (xAxis !== undefined) {
    result.xAxis = xAxis;
  }

  if (yAxis !== undefined) {
    result.yAxis = yAxis;
  }

  return result;
};

/**
 * Reverse parse UI schema field from snake_case to camelCase with lossless transformation
 */
const reverseParseUISchemaField = (
  field: DataObjectUISchemaFieldRaw
): DataObjectUISchemaField => {
  const { chart_config: chartConfig, ...unknownProps } = field;

  const result: DataObjectUISchemaField = { ...unknownProps };

  if (chartConfig) {
    result.chartConfig = reverseParseChartConfig(chartConfig);
  }

  return result;
};

/**
 * Reverse parse section from snake_case to camelCase with lossless transformation
 */
const reverseParseSection = (
  section: DataObjectUISchemaSectionRaw
): DataObjectUISchemaSection => {
  const { display_name: displayName, ...unknownProps } = section;

  const result: DataObjectUISchemaSection = { ...unknownProps };

  if (displayName !== undefined) {
    result.displayName = displayName;
  }

  return result;
};

/**
 * Reverse parse tab from snake_case to camelCase with lossless transformation
 */
const reverseParseTab = (
  tab: DataObjectDetailUISchemaTabRaw
): DataObjectDetailUISchemaTab => {
  const {
    initializer_workflow_kind: initializerWorkflowKind,
    ...unknownProps
  } = tab;

  const result: DataObjectDetailUISchemaTab = { ...unknownProps };

  if (initializerWorkflowKind !== undefined) {
    result.initializerWorkflowKind = initializerWorkflowKind;
  }

  return result;
};

/**
 * Reverse parse workflows config from snake_case to camelCase with lossless transformation
 */
const reverseParseWorkflowsConfig = (
  workflows: DataObjectWorkflowViewConfigRaw
): DataObjectWorkflowViewConfig | undefined => {
  if (!workflows) return undefined;

  const { views, ...unknownProps } = workflows;

  let viewsResult: DataObjectWorkflowViewConfig["views"];

  if (views) {
    const {
      list,
      max_visible_view_tabs: maxVisibleViewTabs,
      ...unknownViewProps
    } = views;

    viewsResult = { list: [], ...unknownViewProps };

    if (list) {
      viewsResult.list = list.map(({ kind_tag: kindTag, ...unknownProps }) => ({
        ...unknownProps,
        kindTag
      }));
    }

    if (maxVisibleViewTabs !== undefined) {
      viewsResult.maxVisibleViewTabs = maxVisibleViewTabs;
    }

    return { views: viewsResult, ...unknownProps };
  }

  return undefined;
};

/**
 * Reverse parse detail UI schema from snake_case to camelCase with lossless transformation
 */
const reverseParseDetailUISchema = (
  detail: DataObjectDetailUISchemaRaw
): DataObjectDetailUISchema => {
  const { sections, tabs, workflows, breadcrumbs, ...unknownProps } = detail;

  const result: DataObjectDetailUISchema = { ...unknownProps };

  if (sections) {
    result.sections = sections.map(reverseParseSection);
  }

  if (tabs) {
    result.tabs = tabs.map(reverseParseTab);
  }

  if (workflows) {
    result.workflows = reverseParseWorkflowsConfig(workflows);
  }

  if (breadcrumbs !== undefined) {
    result.breadcrumbs = breadcrumbs;
  }

  return result;
};

/**
 * Reverse parse UI schema from snake_case to camelCase with lossless transformation
 */
const reverseParseUISchema = (
  uiSchema: DataObjectUISchemaRaw
): DataObjectUISchema => {
  const {
    display_names: displayNames,
    navigation,
    fields,
    list,
    detail,
    badges,
    ...unknownProps
  } = uiSchema;

  const result: DataObjectUISchema = { ...unknownProps };

  if (displayNames) {
    const { singular, plural, ...unknownDisplayNamesProps } = displayNames;
    result.displayNames = {
      ...unknownDisplayNamesProps,
      singular,
      plural
    };
  }

  if (navigation) {
    const { enabled: isEnabled, order, ...unknownNavProps } = navigation;

    result.navigation = { ...unknownNavProps };

    if (isEnabled !== undefined) {
      result.navigation.isEnabled = isEnabled;
    }

    if (order !== undefined) {
      result.navigation.order = order;
    }
  }

  if (fields) {
    result.fields = {};
    for (const [fieldTag, field] of Object.entries(fields)) {
      result.fields[fieldTag] = reverseParseUISchemaField(field);
    }
  }

  if (list !== undefined) {
    result.list = list;
  }

  if (detail) {
    result.detail = reverseParseDetailUISchema(detail);
  }

  if (badges) {
    const { objects_table: objectsTable, ...restBadges } = badges;
    const badgesResult: DataObjectUISchema["badges"] = { ...restBadges };
    if (objectsTable) {
      const {
        is_visible: isVisible,
        column_position: columnPosition,
        ...restObjectsTable
      } = objectsTable;
      badgesResult.objectsTable = {
        ...restObjectsTable
      };

      if (isVisible !== undefined) {
        badgesResult.objectsTable.isVisible = isVisible;
      }

      if (columnPosition !== undefined) {
        badgesResult.objectsTable.columnPosition = columnPosition;
      }
    }
    result.badges = badgesResult;
  }

  return result;
};

/**
 * Reverse parse select options from snake_case to camelCase with lossless transformation
 */
const reverseTransformSelectOptions = (
  selectOptions: DataObjectFilterConfigSelectOptionRaw
): DataObjectFilterConfigSelectOption => {
  const transformed: DataObjectFilterConfigSelectOption = {};

  for (const [key, value] of Object.entries(selectOptions)) {
    const { option_url: optionUrl, options, ...unknownProps } = value;

    transformed[key] = { ...unknownProps };

    if (optionUrl !== undefined) {
      transformed[key].optionUrl = optionUrl;
    }

    if (options !== undefined) {
      transformed[key].options = options;
    }
  }

  return transformed;
};

/**
 * Reverse parse filter config from snake_case to camelCase with lossless transformation
 */
const reverseParseFilterConfig = (
  filterConfig: DataObjectFilterConfigRaw
): DataObjectFilterConfig => {
  if (!filterConfig) return {};

  const {
    multi_select: multiSelect,
    single_select: singleSelect,
    ...unknownProps
  } = filterConfig;

  const result: DataObjectFilterConfig = { ...unknownProps };

  if (multiSelect) {
    result.multiSelect = reverseTransformSelectOptions(multiSelect);
  }

  if (singleSelect) {
    result.singleSelect = reverseTransformSelectOptions(singleSelect);
  }

  return result;
};

/**
 * Reverse parse DataObjectRaw to DataObjectType
 * Converts known properties from snake_case to camelCase while preserving unknown properties
 * for lossless transformation
 */
export const reverseParseDataObject = (
  dataObject: DataObjectRaw
): DataObjectType => {
  const {
    tag,
    schema,
    ui_schema: uiSchema,
    translated_ui_schema: translatedUiSchema,
    filter_config: filterConfig,
    ...unknownProps // Preserve any unknown properties
  } = dataObject;

  const result: DataObjectType = {
    tag,
    schema: reverseParseDataObjectSchema(schema),
    uiSchema: reverseParseUISchema(uiSchema),
    translatedUiSchema: translatedUiSchema
      ? reverseParseUISchema(translatedUiSchema)
      : undefined,
    filterConfig: reverseParseFilterConfig(filterConfig),
    // Pass through any unknown properties as-is for lossless transformation
    ...unknownProps
  };

  return result;
};
